import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { compose } from 'ramda';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Grid from '@mui/material/Grid';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { auth, useAuthState } from 'api/services/firebase';
import { addPaymentMethod } from 'api/services/payments';
import Loading from 'components/system/Loading';
import Hint from 'components/system/Hint';
import withStripeElements from 'components/hoc/withStripeElements';
import withNotification from 'components/hoc/withNotification';
import { attachCardFormInputs } from 'constants/payments';
import { paymentMethodSchema } from 'schemas/billing.schema';
import useAccountStore from 'store/accountStore';
import colors from 'styles/colors';
import './payments.css';

const cardElementOptions = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const PaymentMethodForm = ({
  hint, contentLoading, onSubmitCallback, showSuccessNotification,
}) => {
  const [paymentMethodSecret, setPaymentMethodSecret] = useState(null);
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const promoStatus = useAccountStore((state) => (state.activeAccount.promoStatus));
  const [user] = useAuthState(auth);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    addPaymentMethod(user)
      .then((secret) => setPaymentMethodSecret(secret))
      .catch((err) => console.log(err));
  }, []);

  const formik = useFormik({
    initialValues: {
      name: '',
      city: '',
      line1: '',
      line2: '',
      country: 'us',
      state: '',
    },
    validationSchema: paymentMethodSchema,
    onSubmit: async ({ name, ...rest }, formikHelpers) => {
      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        return;
      }

      const result = await stripe.confirmCardSetup(paymentMethodSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name,
            address: {
              ...rest,
              postal_code: postalCode,
            },
          },
        },
      });

      if (result.error) {
        setPaymentMethodError(result.error.message);
      } else {
        if (!promoStatus) {
          showSuccessNotification('Card is successfully attached.');
        }
        onSubmitCallback && onSubmitCallback();
      }
    },
  });

  const handleChange = (event) => {
    if (event.complete) {
      setEnabled(true);
      setPaymentMethodError(null);
    } else {
      setEnabled(false);
    }
    setPostalCode(event.value.postalCode);
  };

  const onChange = (e) => {
    formik.handleChange(e);
  };

  return (
    <Box component="form" sx={{ width: '420px', minHeight: '456px' }}>
      {contentLoading
        ? <Loading />
        : (
          <>
            {hint && <Hint>{hint}</Hint>}
            <Typography variant="h5">
              Add Payment Method
            </Typography>
            {
              paymentMethodSecret ? (
                <Grid container direction="row" spacing={2}>
                  <div id="stripeContainer">
                    <span>Card Details*</span>
                    <div
                      id="stripeCardContainer"
                      className={paymentMethodError ? 'stripeCardError' : ''}
                    >
                      <Grid item xs={12} sx={{ height: '40px' }}>
                        <CardElement options={cardElementOptions} onChange={handleChange} />
                      </Grid>
                    </div>
                    {paymentMethodError && (
                      <Typography
                        sx={{ color: `${colors.error.main} !important` }}
                        variant="caption"
                      >
                        {paymentMethodError}
                      </Typography>
                    )}
                  </div>
                  {attachCardFormInputs.map(({
                    component: InputComponent, name, grid, ...config
                  }) => (
                    <Grid
                      item
                      key={name}
                      md={grid}
                      xs={12}
                      direction="column"
                      sx={{ marginBottom: '10px' }}
                    >
                      <InputComponent
                        {...config}
                        sx={{ width: '100%' }}
                        autoComplete="off"
                        value={formik.values[name]}
                        error={formik.touched[name] && formik.errors[name]}
                        name={name}
                        onChange={onChange}
                      />
                    </Grid>
                  ))}
                  <LoadingButton
                    fullWidth
                    id="paymentFormSubmit"
                    variant="contained"
                    type="primary"
                    loadingPosition="end"
                    onClick={formik.handleSubmit}
                    loading={formik.isSubmitting}
                    disabled={!enabled || formik.isSubmitting}
                  >
                    Submit
                  </LoadingButton>
                </Grid>
              ) : <Loading sx={{ minHeight: '400px' }} />
            }
          </>
        )}
    </Box>
  );
};

export default compose(withStripeElements, withNotification)(PaymentMethodForm);
